/** Routes (docs/52 §2.2) and the signed-in guard. */
import type { JSX } from 'preact';
import { useEffect, useLayoutEffect } from 'preact/hooks';
import { match, navigate, location, takeScrollRestore } from './lib/router.ts';
import { can, useSession, type Membership } from './lib/session.ts';
import { useStore } from './lib/store.ts';
import type { ClientAction } from '@ff/contracts';
import { S } from '@ff/app-ui/copy';
import { Frame } from './ui/shell.tsx';
import { StepUpHost } from './ui/stepup.tsx';
import { EmptyState } from '@ff/app-ui';
import { Screen } from './ui/common.tsx';
import { HomeScreen, ActivityScreen } from './screens/home.tsx';
import { LeadsScreen } from './screens/leads.tsx';
import { ReviewsScreen, AskScreen, SentScreen, ReviewTemplatesScreen } from './screens/reviews.tsx';
import { AskSeveralScreen, ReviewCardScreen } from './screens/asks.tsx';
import { JobsScreen, AddJobScreen } from './screens/jobs.tsx';
import { PhotoLibraryScreen } from './screens/photos.tsx';
import { InboxScreen, NewRequestScreen } from './screens/inbox.tsx';
import { SignInScreen, CheckEmailScreen, LinkScreen, NoAccessScreen, PaidScreen } from './screens/signin.tsx';
import { StartScreen } from './screens/onboarding.tsx';
import { NotificationsScreen } from './screens/notifications.tsx';
import { HoursScreen } from './screens/quick.tsx';
import { ReportsScreen, ReportScreen, MapScreen, Day90Screen } from './screens/reports.tsx';
import {
  AccountScreen, BusinessScreen, OwnershipScreen, TeamScreen, BillingScreen, CancelScreen, PrefsScreen, SecurityScreen, ExportScreen, HelpScreen, AppearanceScreen,
} from './screens/account.tsx';

type Params = Record<string, string>;
interface RouteDef {
  path: string;
  view: (p: Params) => JSX.Element;
  action?: ClientAction;
}

const ROUTES: RouteDef[] = [
  { path: '/', view: () => <HomeScreen />, action: 'home.read' },
  { path: '/activity', view: () => <ActivityScreen />, action: 'activity.read' },
  { path: '/start', view: () => <StartScreen step={null} />, action: 'onboarding.read' },
  { path: '/start/:step', view: (p) => <StartScreen step={p.step!} />, action: 'onboarding.read' },
  { path: '/leads', view: () => <LeadsScreen /> },
  { path: '/leads/:id', view: (p) => <LeadsScreen id={p.id} /> },
  { path: '/reviews', view: () => <ReviewsScreen />, action: 'reviews.read' },
  { path: '/reviews/ask', view: () => <AskScreen />, action: 'review_requests.create' },
  // c133: several at once and the review card. Both sit before '/reviews/:id' so "card" is never read as a review.
  { path: '/reviews/ask/several', view: () => <AskSeveralScreen />, action: 'review_requests.bulk' },
  { path: '/reviews/card', view: () => <ReviewCardScreen />, action: 'review_requests.create' },
  { path: '/reviews/sent', view: () => <SentScreen />, action: 'review_requests.read' },
  { path: '/reviews/templates', view: () => <ReviewTemplatesScreen />, action: 'review_templates.edit' },
  { path: '/reviews/:id', view: (p) => <ReviewsScreen id={p.id} />, action: 'reviews.read' },
  { path: '/jobs', view: () => <JobsScreen />, action: 'jobs.read' },
  { path: '/jobs/new', view: () => <AddJobScreen />, action: 'jobs.create' },
  { path: '/jobs/photos', view: () => <PhotoLibraryScreen />, action: 'media.read' },
  { path: '/jobs/:id', view: (p) => <JobsScreen id={p.id} />, action: 'jobs.read' },
  { path: '/inbox', view: () => <InboxScreen />, action: 'inbox.read' },
  { path: '/inbox/new', view: () => <NewRequestScreen />, action: 'requests.create_billing' },
  { path: '/inbox/:id', view: (p) => <InboxScreen id={p.id} />, action: 'inbox.read' },
  { path: '/hours', view: () => <HoursScreen />, action: 'hours.read' },
  // The emergency banner (S72) is hidden until it works end to end (deck ruling c079). BannerScreen stays in quick.tsx.
  { path: '/reports', view: () => <ReportsScreen />, action: 'reports.read' },
  { path: '/reports/map', view: () => <MapScreen />, action: 'reports.read' },
  { path: '/reports/day-90', view: () => <Day90Screen />, action: 'reports.read' },
  { path: '/reports/:month', view: (p) => <ReportScreen month={p.month!} />, action: 'reports.read' },
  { path: '/notifications', view: () => <NotificationsScreen /> },
  { path: '/account', view: () => <AccountScreen /> },
  { path: '/account/business', view: () => <BusinessScreen />, action: 'facts.read' },
  { path: '/account/ownership', view: () => <OwnershipScreen />, action: 'home.read' },
  { path: '/account/team', view: () => <TeamScreen />, action: 'team.read' },
  { path: '/account/billing', view: () => <BillingScreen /> },
  { path: '/account/billing/cancel', view: () => <CancelScreen />, action: 'billing.cancel' },
  { path: '/account/notifications', view: () => <PrefsScreen /> },
  { path: '/account/security', view: () => <SecurityScreen /> },
  { path: '/account/appearance', view: () => <AppearanceScreen /> },
  { path: '/account/export', view: () => <ExportScreen /> },
  { path: '/help', view: () => <HelpScreen /> },
];

/** Where a role lands when a screen isn't theirs (crew → Jobs, bookkeeper → Billing). */
function homeFor(m: Membership): string {
  if (m.role === 'crew') return '/jobs';
  if (m.role === 'bookkeeper') return '/account/billing';
  return '/';
}

function resolve(path: string): { def: RouteDef; params: Params } | null {
  for (const def of ROUTES) {
    const params = match(def.path, path);
    if (params) return { def, params };
  }
  return null;
}

function useScrollRestore(key: string) {
  useLayoutEffect(() => {
    const y = takeScrollRestore();
    if (y !== null) window.scrollTo(0, y);
  }, [key]);
}

export function App(): JSX.Element {
  const s = useSession();
  const loc = useStore(location);
  useScrollRestore(loc.key);
  const publicRoute = loc.path.startsWith('/signin');

  useEffect(() => {
    if (s.status === 'signed_out' && !publicRoute) navigate(`/signin${loc.path !== '/' ? `?next=${encodeURIComponent(loc.path)}` : ''}`, { replace: true });
    if (s.status === 'ready' && publicRoute && loc.path !== '/signin/link') navigate(loc.query.get('next') ?? homeFor(s.membership), { replace: true });
    if (s.status === 'ready' && loc.path === '/' && s.membership.role !== 'owner' && s.membership.role !== 'manager') navigate(homeFor(s.membership), { replace: true });
  }, [s.status, loc.path]);

  if (loc.path === '/signin') return <SignInScreen />;
  if (loc.path === '/signin/check') return <CheckEmailScreen />;
  if (loc.path === '/signin/paid') return <PaidScreen />;
  const link = match('/signin/link/:token', loc.path);
  if (link) return <LinkScreen token={link.token!} />;

  if (s.status === 'loading' || s.status === 'signed_out')
    return (
      <div class="boot" aria-busy="true">
        <span class="sr-only">{S.app.loading}</span>
      </div>
    );
  if (s.status === 'offline_unknown')
    return (
      <div class="boot">
        <p>{S.app.offlineEmpty}</p>
      </div>
    );
  if (s.status === 'no_access') return <NoAccessScreen />;

  const r = resolve(loc.path);
  let view: JSX.Element;
  if (!r)
    view = (
      <Screen title={S.app.notFoundTitle}>
        <EmptyState title={S.app.notFoundTitle} body={S.app.notFoundBody} />
      </Screen>
    );
  else if (r.def.action && !can(s.membership, r.def.action))
    view = (
      <Screen title={S.app.notFoundTitle}>
        <EmptyState title={S.errors.permission(S.errors.owner)} body={S.app.notFoundBody} />
      </Screen>
    );
  else view = r.def.view(r.params);

  return (
    <Frame>
      {view}
      <StepUpHost />
    </Frame>
  );
}
